import { createContext, useContext } from 'react';
import { reaction } from 'mobx';
import AppStore from './app.store';
import AuthStore from './auth.store';
import StreamStore from './stream.store';
import SchedulerStore from './scheduler.store';
import MediaStore from './media.store';
import SyncStore from './sync.store';
import OtaStore from './ota.store';
import PlaybackStore, { playbackStore } from './playback.store';
import { triggerStore } from './trigger.store';
import { kioskStore } from './kiosk.store';
import { interactiveElementsStore } from './interactiveElements.store';
import { weatherStore } from './weather.store';

export const appStore = new AppStore();
export const authStore = new AuthStore();
export const streamStore = new StreamStore();
export const schedulerStore = new SchedulerStore();
export const mediaStore = new MediaStore();
export const syncStore = new SyncStore();
export const otaStore = new OtaStore();
/** Sprint 2 CP8 — singleton vem do arquivo (sem instanciar 2x). Loop em CP9. */
export { playbackStore };

// V088 (2026-08-08) — injeta o schedulerStore no triggerStore pra o gatilho
// enriquecer o payload com weather/validade/dia/hora do conteúdo antes de
// setar a playlist. Sem essa linha, `TriggerStore.schedulerStore` fica null
// e o gatilho volta ao comportamento legado (sem filtro do conteúdo).
triggerStore.setSchedulerStore(schedulerStore);

export const stores = {
  appStore,
  authStore,
  streamStore,
  schedulerStore,
  mediaStore,
  syncStore,
  otaStore,
  playbackStore,
  triggerStore,
  kioskStore,
  interactiveElementsStore,
  weatherStore,
};

// v1.0.148 — device registrado mas PENDENTE de aprovação trava o kiosk
// defensivamente (cliente não mexe na janela "Aguardando aprovação"). Operador
// sai com 7 cliques (sem PIN, pois ainda não há PIN). Wiring no composition root
// pra evitar acoplamento entre auth.store e kiosk.store. fireImmediately cobre o
// estado inicial. Quando aprovado, pendingApproval=false → serverEnabled manda.
reaction(
  () => authStore.registrationStatus,
  (status) => kioskStore.setPendingApproval(status === 'pending_approval'),
  { fireImmediately: true },
);

// Renato 2026-06-12 — Injeta o estado de auth no helper de supressão de logs
// (Sync/Analytics/RemoteOps consultam pra rebaixar 401 esperado pra info
// quando device ainda não foi aprovado).
import {
  setDeviceAuthStateProvider,
  setAuthRecoveryHandler,
} from '~/utils/deviceApprovalState';
setDeviceAuthStateProvider(() => ({
  isAuthenticated: authStore.isAuthenticated,
  registrationStatus: authStore.registrationStatus,
}));

// Renato 2026-06-12 — Recovery em 401 inesperado (player deletado no admin /
// token revogado). Cooldown de 2min fica no próprio handleServerRevocation.
setAuthRecoveryHandler((reason) => authStore.handleServerRevocation(reason));

export type RootStore = typeof stores;

export const StoreContext = createContext<RootStore>(stores);

export const StoreProvider = StoreContext.Provider;

export const useStore = (): RootStore => {
  const context = useContext(StoreContext);
  if (!context) {
    throw new Error('useStore must be used within a StoreProvider');
  }
  return context;
};

export {
  AppStore,
  AuthStore,
  StreamStore,
  SchedulerStore,
  MediaStore,
  SyncStore,
  OtaStore,
  PlaybackStore,
  triggerStore,
  kioskStore,
  interactiveElementsStore,
  weatherStore,
};
